'use client';

import { useState, useEffect, useCallback } from 'react';
import { usePrivateMode, PRIVATE_MODE_PIN } from '@/lib/privateMode';
import { clearScratchpad } from '@/lib/scratchpad';

/**
 * Header icon + dialog to toggle private mode. Enabling requires the PIN; disabling
 * is one click (hiding never needs a secret). Toggling reloads the page so every
 * view re-fetches with — or without — the X-Private-Mode header and no private data
 * lingers in memory.
 */
export default function PrivateModeToggle() {
  const enabled = usePrivateMode((s) => s.enabled);
  const setEnabled = usePrivateMode((s) => s.setEnabled);

  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);

  // Avoid SSR/client hydration mismatch: only reflect the persisted value after mount.
  useEffect(() => setMounted(true), []);

  const isOn = mounted && enabled;

  const close = useCallback(() => {
    setOpen(false);
    setPin('');
    setError(false);
  }, []);

  const applyAndReload = useCallback((next: boolean) => {
    // Leaving private mode takes the scratchpad's copied prompts with it.
    if (!next) clearScratchpad();
    setEnabled(next);
    // Persisted synchronously to localStorage; reload so all data re-fetches.
    window.location.reload();
  }, [setEnabled]);

  const submitPin = useCallback(() => {
    if (pin === PRIVATE_MODE_PIN) {
      applyAndReload(true);
    } else {
      setError(true);
      setPin('');
    }
  }, [pin, applyAndReload]);

  // Keyboard: Escape closes, Enter submits the PIN (when locked).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        submitPin();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, submitPin]);

  return (
    <>
      <button
        onClick={() => (isOn ? applyAndReload(false) : setOpen(true))}
        className={`flex items-center gap-1.5 px-2 py-2 transition-subtle rounded-lg hover:bg-[var(--bg-hover)] cursor-pointer ${
          isOn
            ? 'text-[var(--accent)] bg-[var(--accent-soft)]'
            : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
        }`}
        title={isOn ? 'Private mode is ON — click to turn off' : 'Private mode is off'}
        aria-label="Toggle private mode"
      >
        {isOn ? (
          // Unlocked padlock
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
        ) : (
          // Locked padlock
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
        )}
        {isOn && <span className="text-xs font-medium">Private</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
          onClick={close}
        >
          <div
            className="bg-[var(--bg-surface)] rounded-xl w-full max-w-sm overflow-hidden border border-[var(--border-subtle)] shadow-soft animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--bg-elevated)]">
                <svg className="w-6 h-6 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </div>

              <h3 className="text-lg font-semibold mb-2">Enter PIN</h3>
              <p className="text-sm text-[var(--text-secondary)]">
                Unlock private series, their images and history, and restricted lists.
              </p>

              <div className="mt-4">
                <input
                  type="password"
                  inputMode="numeric"
                  autoFocus
                  value={pin}
                  onChange={(e) => { setPin(e.target.value); setError(false); }}
                  placeholder="••••"
                  className="input w-full text-center tracking-[0.5em] text-lg"
                />
                {error && (
                  <p className="text-xs text-[var(--error)] mt-2">Incorrect PIN</p>
                )}
              </div>
            </div>

            <div className="flex gap-3 p-4 bg-[var(--bg-elevated)] border-t border-[var(--border-subtle)]">
              <button onClick={close} className="btn btn-ghost flex-1">
                Cancel
              </button>
              <button
                onClick={submitPin}
                disabled={!pin}
                className="btn flex-1 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Unlock
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
